// Writing an .xlsx in the browser (design 02 12.1): formulas with their
// computed values, styles, column widths, frozen panes, defined names, Excel
// tables (the 거래 sheet), comments (the original formula of a cell Excel
// cannot compute) and dynamic-array formulas that spill in Excel too.

import { toFileFormula } from "./formula";
import { cellName, colName, type XName, type XStyle } from "./model";
import { BUILTIN_FORMATS } from "./read";
import { esc, XML_HEAD } from "./xml";
import { writeZip, type ZipEntry } from "./zip";

export type XValue = number | string | boolean | { error: string } | null;

export interface WCell {
  row: number;
  col: number;
  /** without "=", as a person types it; written with Excel's prefixes */
  formula?: string;
  /** the computed value (or the value of a plain cell) */
  value: XValue;
  /** a formula's spill size, when it returns an array (rows x cols) */
  spill?: { rows: number; cols: number };
  style?: XStyle;
  /** a comment on the cell */
  note?: string;
}

export interface WTable {
  name: string;
  /** first and last cell, header row included: "A1:H120" */
  ref: string;
  columns: string[];
}

export interface WSheet {
  name: string;
  cells: WCell[];
  colWidths?: Record<string, number>;
  frozenRows?: number;
  frozenCols?: number;
  tables?: WTable[];
}

export interface WBook {
  sheets: WSheet[];
  names?: XName[];
}

const NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const NS_R = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const PKG_REL = "http://schemas.openxmlformats.org/package/2006/relationships";
const CT_PREFIX = "application/vnd.openxmlformats-officedocument.spreadsheetml";
const EXCEL_STRING_MAX = 32_767;

const ERRORS = ["#NULL!", "#DIV/0!", "#VALUE!", "#REF!", "#NAME?", "#NUM!", "#N/A", "#GETTING_DATA", "#SPILL!", "#CALC!"];

function excelError(e: string): string {
  // ours that Excel does not know (#DIM!) become #VALUE!
  return ERRORS.includes(e) ? e : "#VALUE!";
}

class Styles {
  private numFmts = new Map<string, number>();
  private fonts: string[] = ['<font><sz val="11"/><name val="맑은 고딕"/><family val="3"/><charset val="129"/></font>'];
  private fills: string[] = ['<fill><patternFill patternType="none"/></fill>', '<fill><patternFill patternType="gray125"/></fill>'];
  private xfs: string[] = ['<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'];
  private byKey = new Map<string, number>([["", 0]]);
  private builtin = new Map<string, number>(Object.entries(BUILTIN_FORMATS).map(([id, code]) => [code, Number(id)]));

  index(s: XStyle | undefined): number {
    if (s === undefined) return 0;
    const key = JSON.stringify([s.nf, s.b, s.i, s.u, s.fg, s.bg, s.al, s.wrap]);
    const known = this.byKey.get(key);
    if (known !== undefined) return known;
    let numFmtId = 0;
    if (s.nf !== undefined && s.nf !== "") {
      numFmtId = this.builtin.get(s.nf) ?? this.numFmts.get(s.nf) ?? 164 + this.numFmts.size;
      if (numFmtId >= 164) this.numFmts.set(s.nf, numFmtId);
    }
    let fontId = 0;
    if (s.b || s.i || s.u || s.fg) {
      const font =
        "<font>" +
        (s.b ? "<b/>" : "") +
        (s.i ? "<i/>" : "") +
        (s.u ? "<u/>" : "") +
        '<sz val="11"/>' +
        (s.fg ? `<color rgb="FF${s.fg.slice(1).toUpperCase()}"/>` : "") +
        '<name val="맑은 고딕"/><family val="3"/><charset val="129"/></font>';
      fontId = this.fonts.indexOf(font);
      if (fontId < 0) fontId = this.fonts.push(font) - 1;
    }
    let fillId = 0;
    if (s.bg) {
      const fill = `<fill><patternFill patternType="solid"><fgColor rgb="FF${s.bg.slice(1).toUpperCase()}"/><bgColor indexed="64"/></patternFill></fill>`;
      fillId = this.fills.indexOf(fill);
      if (fillId < 0) fillId = this.fills.push(fill) - 1;
    }
    const horizontal = s.al === "l" ? "left" : s.al === "c" ? "center" : s.al === "r" ? "right" : undefined;
    const align =
      horizontal !== undefined || s.wrap
        ? `<alignment${horizontal ? ` horizontal="${horizontal}"` : ""}${s.wrap ? ' wrapText="1"' : ""}/>`
        : "";
    const xf =
      `<xf numFmtId="${numFmtId}" fontId="${fontId}" fillId="${fillId}" borderId="0" xfId="0"` +
      (numFmtId ? ' applyNumberFormat="1"' : "") +
      (fontId ? ' applyFont="1"' : "") +
      (fillId ? ' applyFill="1"' : "") +
      (align ? ` applyAlignment="1">${align}</xf>` : "/>");
    const id = this.xfs.push(xf) - 1;
    this.byKey.set(key, id);
    return id;
  }

  xml(): string {
    const fmts = [...this.numFmts.entries()]
      .map(([code, id]) => `<numFmt numFmtId="${id}" formatCode="${esc(code)}"/>`)
      .join("");
    return (
      XML_HEAD +
      `<styleSheet xmlns="${NS}">` +
      (this.numFmts.size ? `<numFmts count="${this.numFmts.size}">${fmts}</numFmts>` : "") +
      `<fonts count="${this.fonts.length}">${this.fonts.join("")}</fonts>` +
      `<fills count="${this.fills.length}">${this.fills.join("")}</fills>` +
      '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
      '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
      `<cellXfs count="${this.xfs.length}">${this.xfs.join("")}</cellXfs>` +
      '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
      "</styleSheet>"
    );
  }
}

class Strings {
  private list: string[] = [];
  private index = new Map<string, number>();
  count = 0;

  add(s: string): number {
    this.count++;
    const known = this.index.get(s);
    if (known !== undefined) return known;
    const id = this.list.push(s) - 1;
    this.index.set(s, id);
    return id;
  }

  xml(): string {
    const items = this.list
      .map((s) => `<si><t${/^\s|\s$|\n/.test(s) ? ' xml:space="preserve"' : ""}>${esc(s)}</t></si>`)
      .join("");
    return XML_HEAD + `<sst xmlns="${NS}" count="${this.count}" uniqueCount="${this.list.length}">${items}</sst>`;
  }
}

/** A sheet name as a formula writes it: 시트1 or '2026 지출' */
export function quoteSheet(name: string): string {
  return /^[\p{L}_][\p{L}\p{N}_.]*$/u.test(name) && !/^[A-Za-z]{1,3}[0-9]+$/.test(name)
    ? name
    : `'${name.replace(/'/g, "''")}'`;
}

function vmlFor(notes: WCell[]): string {
  const shapes = notes
    .map(
      (c, i) =>
        `<v:shape id="_x0000_s${1025 + i}" type="#_x0000_t202" style="position:absolute;margin-left:59.25pt;margin-top:1.5pt;width:180pt;height:60pt;z-index:${i + 1};visibility:hidden" fillcolor="#ffffe1" o:insetmode="auto">` +
        '<v:fill color2="#ffffe1"/><v:shadow on="t" color="black" obscured="t"/><v:path o:connecttype="none"/>' +
        '<v:textbox style="mso-direction-alt:auto"><div style="text-align:left"></div></v:textbox>' +
        '<x:ClientData ObjectType="Note"><x:MoveWithCells/><x:SizeWithCells/>' +
        `<x:Anchor>${c.col + 1}, 15, ${c.row}, 2, ${c.col + 4}, 15, ${c.row + 4}, 16</x:Anchor>` +
        `<x:AutoFill>False</x:AutoFill><x:Row>${c.row}</x:Row><x:Column>${c.col}</x:Column></x:ClientData></v:shape>`,
    )
    .join("");
  return (
    '<xml xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">' +
    '<o:shapelayout v:ext="edit"><o:idmap v:ext="edit" data="1"/></o:shapelayout>' +
    '<v:shapetype id="_x0000_t202" coordsize="21600,21600" o:spt="202" path="m,l,21600r21600,l21600,xe">' +
    '<v:stroke joinstyle="miter"/><v:path gradientshapeok="t" o:connecttype="rect"/></v:shapetype>' +
    shapes +
    "</xml>"
  );
}

const METADATA =
  XML_HEAD +
  `<metadata xmlns="${NS}" xmlns:xda="http://schemas.microsoft.com/office/spreadsheetml/2017/dynamicarray">` +
  '<metadataTypes count="1"><metadataType name="XLDAPR" minSupportedVersion="120000" copy="1" pasteAll="1" ' +
  'pasteValues="1" merge="1" splitFirst="1" rowColShift="1" clearFormats="1" clearComments="1" assign="1" ' +
  'coerce="1" cellMeta="1"/></metadataTypes>' +
  '<futureMetadata name="XLDAPR" count="1"><bk><extLst><ext uri="{bdbb8cdc-fa1e-496e-a857-3c3f30c029c3}">' +
  '<xda:dynamicArrayProperties fDynamic="1" fCollapsed="0"/></ext></extLst></bk></futureMetadata>' +
  '<cellMetadata count="1"><bk><rc t="1" v="0"/></bk></cellMetadata></metadata>';

/** The .xlsx file of the book */
export async function writeXlsx(book: WBook): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const files: ZipEntry[] = [];
  const put = (name: string, text: string): void => {
    files.push({ name, data: enc.encode(text) });
  };
  const styles = new Styles();
  const strings = new Strings();
  const overrides: string[] = [];
  const bookRels: string[] = [];
  let usesDynamic = false;
  let tableId = 0;
  let vml = false;

  book.sheets.forEach((sheet, si) => {
    const n = si + 1;
    const cells = [...sheet.cells].sort((a, b) => a.row - b.row || a.col - b.col);
    const rows: string[] = [];
    let current = -1;
    let rowCells: string[] = [];
    const flush = (): void => {
      if (current >= 0) rows.push(`<row r="${current + 1}">${rowCells.join("")}</row>`);
      rowCells = [];
    };
    let maxRow = 0;
    let maxCol = 0;
    for (const c of cells) {
      if (c.row !== current) {
        flush();
        current = c.row;
      }
      maxRow = Math.max(maxRow, c.row);
      maxCol = Math.max(maxCol, c.col);
      const ref = cellName(c.row, c.col);
      const s = styles.index(c.style);
      const sAttr = s ? ` s="${s}"` : "";
      let f = "";
      let cm = "";
      if (c.formula !== undefined && c.formula !== "") {
        const text = esc(toFileFormula(c.formula));
        if (c.spill !== undefined && (c.spill.rows > 1 || c.spill.cols > 1)) {
          const end = cellName(c.row + c.spill.rows - 1, c.col + c.spill.cols - 1);
          f = `<f t="array" ref="${ref}:${end}">${text}</f>`;
          cm = ' cm="1"';
          usesDynamic = true;
        } else {
          f = `<f>${text}</f>`;
        }
      }
      const v = c.value;
      if (v === null || v === undefined) {
        rowCells.push(f ? `<c r="${ref}"${sAttr}${cm}>${f}</c>` : `<c r="${ref}"${sAttr}/>`);
      } else if (typeof v === "number") {
        if (Number.isFinite(v)) rowCells.push(`<c r="${ref}"${sAttr}${cm}>${f}<v>${v}</v></c>`);
        else rowCells.push(`<c r="${ref}"${sAttr} t="e"${cm}>${f}<v>#NUM!</v></c>`);
      } else if (typeof v === "boolean") {
        rowCells.push(`<c r="${ref}"${sAttr} t="b"${cm}>${f}<v>${v ? 1 : 0}</v></c>`);
      } else if (typeof v === "string") {
        const text = v.length > EXCEL_STRING_MAX ? v.slice(0, EXCEL_STRING_MAX) : v;
        if (f) rowCells.push(`<c r="${ref}"${sAttr} t="str"${cm}>${f}<v>${esc(text)}</v></c>`);
        else rowCells.push(`<c r="${ref}"${sAttr} t="s"><v>${strings.add(text)}</v></c>`);
      } else {
        rowCells.push(`<c r="${ref}"${sAttr} t="e"${cm}>${f}<v>${esc(excelError(v.error))}</v></c>`);
      }
    }
    flush();

    const frozenRows = sheet.frozenRows ?? 0;
    const frozenCols = sheet.frozenCols ?? 0;
    let view = '<sheetView workbookViewId="0"' + (si === 0 ? ' tabSelected="1"' : "") + "/>";
    if (frozenRows > 0 || frozenCols > 0) {
      const top = cellName(frozenRows, frozenCols);
      const pane = frozenRows > 0 && frozenCols > 0 ? "bottomRight" : frozenRows > 0 ? "bottomLeft" : "topRight";
      view =
        '<sheetView workbookViewId="0"' +
        (si === 0 ? ' tabSelected="1"' : "") +
        ">" +
        `<pane${frozenCols ? ` xSplit="${frozenCols}"` : ""}${frozenRows ? ` ySplit="${frozenRows}"` : ""} topLeftCell="${top}" activePane="${pane}" state="frozen"/>` +
        `<selection pane="${pane}" activeCell="${top}" sqref="${top}"/></sheetView>`;
    }
    const widths = Object.entries(sheet.colWidths ?? {})
      .map(([col, px]) => [Number(col), px] as const)
      .filter(([col]) => Number.isInteger(col) && col >= 0 && col < 16_384)
      .sort((a, b) => a[0] - b[0])
      .map(([col, px]) => {
        // Excel's stored width counts the padding too (7 px digits, Calibri 11)
        const w = Math.trunc((px / 7) * 256) / 256;
        return `<col min="${col + 1}" max="${col + 1}" width="${w}" customWidth="1"/>`;
      })
      .join("");

    const rels: string[] = [];
    const notes = cells.filter((c) => c.note !== undefined && c.note !== "");
    let legacy = "";
    if (notes.length > 0) {
      vml = true;
      const authors = '<authors><author>tensor</author></authors>';
      const list = notes
        .map(
          (c) =>
            `<comment ref="${cellName(c.row, c.col)}" authorId="0"><text><t xml:space="preserve">${esc(c.note ?? "")}</t></text></comment>`,
        )
        .join("");
      put(`xl/comments${n}.xml`, XML_HEAD + `<comments xmlns="${NS}">${authors}<commentList>${list}</commentList></comments>`);
      put(`xl/drawings/vmlDrawing${n}.vml`, vmlFor(notes));
      overrides.push(`<Override PartName="/xl/comments${n}.xml" ContentType="${CT_PREFIX}.comments+xml"/>`);
      rels.push(`<Relationship Id="rId${rels.length + 1}" Type="${REL}/comments" Target="../comments${n}.xml"/>`);
      rels.push(`<Relationship Id="rId${rels.length + 1}" Type="${REL}/vmlDrawing" Target="../drawings/vmlDrawing${n}.vml"/>`);
      legacy = `<legacyDrawing r:id="rId${rels.length}"/>`;
    }
    const tableParts: string[] = [];
    for (const t of sheet.tables ?? []) {
      tableId++;
      const cols = t.columns.map((name, i) => `<tableColumn id="${i + 1}" name="${esc(name)}"/>`).join("");
      put(
        `xl/tables/table${tableId}.xml`,
        XML_HEAD +
          `<table xmlns="${NS}" id="${tableId}" name="${esc(t.name)}" displayName="${esc(t.name)}" ref="${t.ref}" totalsRowShown="0">` +
          `<autoFilter ref="${t.ref}"/><tableColumns count="${t.columns.length}">${cols}</tableColumns>` +
          '<tableStyleInfo name="TableStyleMedium2" showFirstColumn="0" showLastColumn="0" showRowStripes="1" showColumnStripes="0"/></table>',
      );
      overrides.push(`<Override PartName="/xl/tables/table${tableId}.xml" ContentType="${CT_PREFIX}.table+xml"/>`);
      rels.push(`<Relationship Id="rId${rels.length + 1}" Type="${REL}/table" Target="../tables/table${tableId}.xml"/>`);
      tableParts.push(`<tablePart r:id="rId${rels.length}"/>`);
    }
    const dim = cells.length ? `A1:${colName(maxCol)}${maxRow + 1}` : "A1";
    put(
      `xl/worksheets/sheet${n}.xml`,
      XML_HEAD +
        `<worksheet xmlns="${NS}" xmlns:r="${NS_R}">` +
        `<dimension ref="${dim}"/><sheetViews>${view}</sheetViews>` +
        '<sheetFormatPr defaultRowHeight="16.5"/>' +
        (widths ? `<cols>${widths}</cols>` : "") +
        `<sheetData>${rows.join("")}</sheetData>` +
        '<pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>' +
        legacy +
        (tableParts.length ? `<tableParts count="${tableParts.length}">${tableParts.join("")}</tableParts>` : "") +
        "</worksheet>",
    );
    if (rels.length > 0)
      put(
        `xl/worksheets/_rels/sheet${n}.xml.rels`,
        XML_HEAD + `<Relationships xmlns="${PKG_REL}">${rels.join("")}</Relationships>`,
      );
    overrides.push(`<Override PartName="/xl/worksheets/sheet${n}.xml" ContentType="${CT_PREFIX}.worksheet+xml"/>`);
    bookRels.push(`<Relationship Id="rId${n}" Type="${REL}/worksheet" Target="worksheets/sheet${n}.xml"/>`);
  });

  const count = book.sheets.length;
  bookRels.push(`<Relationship Id="rId${count + 1}" Type="${REL}/styles" Target="styles.xml"/>`);
  bookRels.push(`<Relationship Id="rId${count + 2}" Type="${REL}/sharedStrings" Target="sharedStrings.xml"/>`);
  if (usesDynamic) {
    bookRels.push(`<Relationship Id="rId${count + 3}" Type="${REL}/sheetMetadata" Target="metadata.xml"/>`);
    put("xl/metadata.xml", METADATA);
    overrides.push(`<Override PartName="/xl/metadata.xml" ContentType="${CT_PREFIX}.sheetMetadata+xml"/>`);
  }
  const sheets = book.sheets
    .map((s, i) => `<sheet name="${esc(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join("");
  const names = (book.names ?? [])
    .map((d) => `<definedName name="${esc(d.name)}">${esc(toFileFormula(d.formula))}</definedName>`)
    .join("");
  put(
    "xl/workbook.xml",
    XML_HEAD +
      `<workbook xmlns="${NS}" xmlns:r="${NS_R}"><bookViews><workbookView/></bookViews>` +
      `<sheets>${sheets}</sheets>` +
      (names ? `<definedNames>${names}</definedNames>` : "") +
      '<calcPr calcId="191029" fullCalcOnLoad="1"/></workbook>',
  );
  put("xl/_rels/workbook.xml.rels", XML_HEAD + `<Relationships xmlns="${PKG_REL}">${bookRels.join("")}</Relationships>`);
  put("xl/styles.xml", styles.xml());
  put("xl/sharedStrings.xml", strings.xml());
  put(
    "_rels/.rels",
    XML_HEAD +
      `<Relationships xmlns="${PKG_REL}"><Relationship Id="rId1" Type="${REL}/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
  );
  put(
    "[Content_Types].xml",
    XML_HEAD +
      '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
      '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
      '<Default Extension="xml" ContentType="application/xml"/>' +
      (vml ? '<Default Extension="vml" ContentType="application/vnd.openxmlformats-officedocument.vmlDrawing"/>' : "") +
      `<Override PartName="/xl/workbook.xml" ContentType="${CT_PREFIX}.sheet.main+xml"/>` +
      `<Override PartName="/xl/styles.xml" ContentType="${CT_PREFIX}.styles+xml"/>` +
      `<Override PartName="/xl/sharedStrings.xml" ContentType="${CT_PREFIX}.sharedStrings+xml"/>` +
      overrides.join("") +
      "</Types>",
  );
  // [Content_Types].xml first, as Excel writes it
  files.sort((a, b) => (a.name === "[Content_Types].xml" ? -1 : b.name === "[Content_Types].xml" ? 1 : 0));
  return writeZip(files);
}
